/**
 * Combo Generation and Ranking Engine
 * Generates all possible combinations and ranks them by cost
 */

import type { Quote, Combo, ComboRanking, MultiQuoteQuotation } from '../types';
import { calculateLegCost } from './calculations';

/**
 * Generate a unique ID for a combo
 */
function generateComboId(outbound: Quote, returnQuote?: Quote): string {
  if (returnQuote) {
    return `${outbound.id}-${returnQuote.id}`;
  }
  return outbound.id;
}

/**
 * Calculate the total cost and metrics for a combo
 */
function calculateComboMetrics(
  outbound: Quote,
  returnQuote: Quote | undefined,
  airlinePrice: number
): Omit<Combo, 'id' | 'rank' | 'outbound' | 'return'> {
  const outboundCost = calculateLegCost(outbound);
  const returnCost = returnQuote ? calculateLegCost(returnQuote) : 0;
  const totalCost = outboundCost + returnCost;

  // Use Math.max to ensure savings is never negative for display
  const savings = Math.max(0, airlinePrice - totalCost);
  const savingsPercentage = airlinePrice > 0
    ? (savings / airlinePrice) * 100
    : 0;

  const isViable = totalCost < airlinePrice;

  return {
    totalCost,
    savings,
    savingsPercentage,
    isViable,
  };
}

/**
 * Generate all possible combinations from outbound and return quotes
 * Uses Cartesian product algorithm
 */
export function generateCombos(quotation: MultiQuoteQuotation): Combo[] {
  const { outboundQuotes, returnQuotes, airlinePrice, isRoundTrip } = quotation;

  // Validation
  if (outboundQuotes.length === 0) {
    return [];
  }

  const combos: Combo[] = [];

  if (isRoundTrip && returnQuotes.length > 0) {
    // Round-trip: Generate all combinations (Cartesian product)
    for (const outbound of outboundQuotes) {
      for (const returnQuote of returnQuotes) {
        const metrics = calculateComboMetrics(outbound, returnQuote, airlinePrice);

        combos.push({
          id: generateComboId(outbound, returnQuote),
          outbound,
          return: returnQuote,
          ...metrics,
          rank: 0, // Will be assigned during ranking
        });
      }
    }
  } else {
    // One-way: Only outbound quotes
    for (const outbound of outboundQuotes) {
      const metrics = calculateComboMetrics(outbound, undefined, airlinePrice);

      combos.push({
        id: generateComboId(outbound),
        outbound,
        return: undefined,
        ...metrics,
        rank: 0, // Will be assigned during ranking
      });
    }
  }

  return combos;
}

/**
 * Rank combos by total cost (lowest to highest)
 * Assigns rank position to each combo
 */
export function rankCombos(combos: Combo[]): Combo[] {
  // Sort by total cost (ascending - lowest cost first)
  const sorted = [...combos].sort((a, b) => a.totalCost - b.totalCost);

  // Assign rank positions
  return sorted.map((combo, index) => ({
    ...combo,
    rank: index + 1,
  }));
}

/**
 * Generate and rank all combos
 * Main entry point for combo analysis
 */
export function generateComboRanking(quotation: MultiQuoteQuotation): ComboRanking {
  // Generate all possible combinations
  const generatedCombos = generateCombos(quotation);

  // Rank them by cost
  const rankedCombos = rankCombos(generatedCombos);

  // Find the best combo (rank 1, lowest cost)
  const bestCombo = rankedCombos.length > 0 ? rankedCombos[0] : null;

  // Check if any viable option exists
  const hasViableOption = rankedCombos.some(combo => combo.isViable);

  return {
    combos: rankedCombos,
    bestCombo,
    hasViableOption,
  };
}

/**
 * Get top N combos from ranking
 */
export function getTopCombos(ranking: ComboRanking, count: number): Combo[] {
  return ranking.combos.slice(0, count);
}

/**
 * Get only viable combos from ranking
 */
export function getViableCombos(ranking: ComboRanking): Combo[] {
  return ranking.combos.filter(combo => combo.isViable);
}

/**
 * Get combo statistics
 */
export function getComboStats(ranking: ComboRanking): {
  totalCombos: number;
  viableCombos: number;
  nonViableCombos: number;
  bestSavings: number;
  worstCost: number;
} {
  const totalCombos = ranking.combos.length;
  const viableCombos = ranking.combos.filter(c => c.isViable).length;
  const nonViableCombos = totalCombos - viableCombos;

  const bestSavings = ranking.bestCombo?.savings ?? 0;
  const worstCost = ranking.combos.length > 0
    ? ranking.combos[ranking.combos.length - 1].totalCost
    : 0;

  return {
    totalCombos,
    viableCombos,
    nonViableCombos,
    bestSavings,
    worstCost,
  };
}

/**
 * Validate multi-quote quotation
 */
export function validateMultiQuoteQuotation(quotation: MultiQuoteQuotation): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (quotation.airlinePrice <= 0) {
    errors.push('O preço da companhia aérea deve ser maior que zero');
  }

  if (quotation.outboundQuotes.length === 0) {
    errors.push('Adicione pelo menos uma cotação de ida');
  }

  // Validate each outbound quote
  quotation.outboundQuotes.forEach((quote, index) => {
    if (quote.miles <= 0) {
      errors.push(`Cotação ${index + 1} da ida: quantidade de milhas inválida`);
    }
    if (quote.costPerThousand <= 0) {
      errors.push(`Cotação ${index + 1} da ida: custo do milheiro inválido`);
    }
    if (quote.boardingTax < 0) {
      errors.push(`Cotação ${index + 1} da ida: taxa de embarque inválida`);
    }
  });

  // Validate return quotes if round-trip
  if (quotation.isRoundTrip) {
    if (quotation.returnQuotes.length === 0) {
      errors.push('Viagem ida e volta requer pelo menos uma cotação de volta');
    }

    quotation.returnQuotes.forEach((quote, index) => {
      if (quote.miles <= 0) {
        errors.push(`Cotação ${index + 1} da volta: quantidade de milhas inválida`);
      }
      if (quote.costPerThousand <= 0) {
        errors.push(`Cotação ${index + 1} da volta: custo do milheiro inválido`);
      }
      if (quote.boardingTax < 0) {
        errors.push(`Cotação ${index + 1} da volta: taxa de embarque inválida`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
